import { useCallback, useEffect, useState } from "react";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Plus, Warning } from "@phosphor-icons/react";
import QualificationDialog from "./QualificationDialog";
import { fmtDate } from "../../lib/dateUtil";

const CELL = {
  valid: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300",
  expiring: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300",
  expired: "bg-red-100 text-red-800 border-red-300 dark:bg-red-950/40 dark:text-red-300",
  no_expiry: "bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300",
};

/** Operative × qualification grid. Click a cell to add or edit; colours show expiry status. */
export default function CompetencyMatrix() {
  const [m, setM] = useState(null);
  const [types, setTypes] = useState([]);
  const [dlg, setDlg] = useState({ open: false, qual: null });
  const [filter, setFilter] = useState("");

  const load = useCallback(() => API.get("/hs/qualifications/matrix").then(({ data }) => setM(data)).catch(() => setM({ columns: [], operatives: [] })), []);
  useEffect(() => { load(); API.get("/hs/qualifications/types").then(({ data }) => setTypes(data.types || [])).catch(() => {}); }, [load]);

  if (!m) return <p className="text-xs text-muted-foreground p-4">Loading…</p>;
  const ops = m.operatives.filter(o => !filter || o.name.toLowerCase().includes(filter.toLowerCase()));
  const expired = m.operatives.reduce((n, o) => n + o.expired, 0);
  const expiring = m.operatives.reduce((n, o) => n + o.expiring, 0);

  return (
    <div className="space-y-3" data-testid="competency-matrix">
      <div className="flex flex-wrap items-center gap-2">
        <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter operatives…" className="h-8 text-xs rounded-md border border-border bg-background px-2.5 w-48" data-testid="matrix-filter" />
        <div className="flex gap-2 text-[11px]">
          {expired > 0 && <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-100 text-red-800" data-testid="matrix-expired-count"><Warning size={12} /> {expired} expired</span>}
          {expiring > 0 && <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-100 text-amber-800" data-testid="matrix-expiring-count">{expiring} expiring within {m.expiring_days} days</span>}
        </div>
        <div className="ml-auto flex items-center gap-3 text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1"><i className={`w-3 h-3 rounded-sm border ${CELL.valid}`} /> Valid</span>
          <span className="inline-flex items-center gap-1"><i className={`w-3 h-3 rounded-sm border ${CELL.expiring}`} /> Expiring</span>
          <span className="inline-flex items-center gap-1"><i className={`w-3 h-3 rounded-sm border ${CELL.expired}`} /> Expired</span>
          <span className="inline-flex items-center gap-1"><i className={`w-3 h-3 rounded-sm border ${CELL.no_expiry}`} /> No expiry</span>
        </div>
        <Button size="sm" onClick={() => setDlg({ open: true, qual: null })} data-testid="qual-add-btn"><Plus size={14} className="mr-1" /> Add qualification</Button>
      </div>

      {m.operatives.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground" data-testid="matrix-empty">No in-house operatives yet — add them under In-House Team → Team.</div>
      ) : (
        <div className="border border-border rounded-lg overflow-x-auto">
          <table className="text-xs min-w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2 sticky left-0 bg-muted/50 backdrop-blur z-10 min-w-[160px] text-[10px] uppercase text-muted-foreground">Operative</th>
                {m.columns.map(c => <th key={c} className="p-2 text-[10px] font-semibold text-muted-foreground min-w-[110px] max-w-[140px] align-bottom"><span className="block leading-tight">{c}</span></th>)}
              </tr>
            </thead>
            <tbody>
              {ops.map(o => (
                <tr key={o.user_id} className="border-t border-border" data-testid={`matrix-row-${o.user_id}`}>
                  <td className="p-2 sticky left-0 bg-card z-10">
                    <p className="font-semibold">{o.name}</p>
                    <p className="text-[10px] text-muted-foreground">{o.held} held{o.expired ? ` · ${o.expired} expired` : ""}{o.expiring ? ` · ${o.expiring} expiring` : ""}</p>
                  </td>
                  {m.columns.map(c => {
                    const q = o.qualifications[c];
                    return (
                      <td key={c} className="p-1 align-top">
                        <button type="button" onClick={() => setDlg({ open: true, qual: q || { user_id: o.user_id, qualification: c, category: (types.find(t => t.name === c) || {}).category || "" } })}
                          className={`w-full h-full min-h-[40px] rounded border text-left px-1.5 py-1 transition-colors ${q ? CELL[q.status] : "border-dashed border-border text-muted-foreground hover:bg-muted/60"}`}
                          data-testid={`matrix-cell-${o.user_id}-${c.replace(/\W+/g, "-").toLowerCase()}`} title={q ? `${c}${q.reference_number ? ` · ${q.reference_number}` : ""}` : `Add ${c} for ${o.name}`}>
                          {q ? (<><span className="block text-[10px] font-semibold">{q.status === "no_expiry" ? "Held" : q.status === "expired" ? "Expired" : q.status === "expiring" ? "Expires" : "Valid"}</span>{q.expiry_date && <span className="block text-[10px]">{fmtDate(q.expiry_date)}</span>}</>) : <span className="text-[10px]">—</span>}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <QualificationDialog open={dlg.open} onOpenChange={(o) => setDlg(d => ({ ...d, open: o }))} qual={dlg.qual} operatives={m.operatives} types={types} onSaved={load} />
    </div>
  );
}
